import { cloneElement, useState } from 'react';
import { connect } from 'react-redux';
import { useModal } from '@belong/common';
import {
  NEXT_MILESTONE_MAP,
  NEXT_REQUIRED_EVENTS_BLACK_LIST,
  TAB_ITEMS,
} from 'accounts/constants/my-home-timeline';
import { RescheduleAppointmentModal } from 'accounts/components/reschedule-appointment-modal/reschedule-appointment-modal';
import { useHomeownerInspections } from 'accounts/hooks/homeowners/use-homeowner-inspections';
import { findOnboardingInspection } from 'accounts/utils/inspections';
import classNames from 'classnames/bind';
import AccountTimeline from 'components/AccountTimeline/AccountTimeline';
import MultipleBelongEmployeeProfileView from 'components/MultipleBelongEmployeeProfileView/MultipleBelongEmployeeProfileView';
import TabBar from 'components/TabBar/TabBar';
import AccountUnitDetailContainer from 'containers/AccountUnitDetailContainer/AccountUnitDetailContainer';
import ResidentListContainer from 'containers/ResidentListContainer/ResidentListContainer';
import Space, { SPACE_TYPES } from 'corecomponents/Space/Space';
import { Text } from 'design-system';
import { flatten, isNil } from 'es-toolkit/compat';
import { EmployeeAssignmentTypes } from 'models/enums';
import AccountPageSection from 'pages/Accounts/Pages/AccountPage/AccountPageSection/AccountPageSection';
import PropTypes from 'prop-types';
import {
  selectAccountProperties,
  selectAccountUnitExternalPublishings,
} from 'store/redux/homeowner-accounts/selectors';
import DisplayEvent from '../DisplayEvent/DisplayEvent';
// import CurrentLease from '../CurrentLease/CurrentLease';
import NextLease from '../NextLease/NextLease';
import NextRequiredEvents from '../NextRequiredEvents/NextRequiredEvents';
import styles from './Unit.module.css';

const cx = classNames.bind(styles);

const Unit = ({
  unit,
  employees,
  timeline,
  externalPublishings,
  properties,
}) => {
  const { currentLease, nextLease } = unit || {};
  const [selecetedTab, setSelectedTab] = useState(TAB_ITEMS[0]);
  const { inspections } = useHomeownerInspections();
  const [isRescheduleOpen, openReschedule, closeReschedule] = useModal();

  if (!unit) return null;

  const { unitId, timeZone } = unit.basicInfo;
  const rescheduleProperty = properties?.find((property) =>
    property.units?.some((item) => item.basicInfo?.unitId === unitId),
  );
  const rescheduleInspection = findOnboardingInspection(inspections, unitId);

  const unitExternalPublishing = externalPublishings?.find(
    (externalPublishing) => externalPublishing.unitId === unit.basicInfo.unitId,
  );

  const {
    residents = [],
    kids = [],
    pets = [],
  } = currentLease || nextLease || {};
  const allResidents = {
    residents,
    kids,
    pets,
  };
  const allResidentsLen = [...residents, ...kids, ...pets].length;

  // We need the index of the first index that is not internal to show the pulse animation.
  const firstNonInternalIndex = timeline.timelineEvents.findIndex(
    (event) => event.name,
  );
  const timelineItems = [
    ...flatten(
      timeline.timelineEvents.map((event, index) => {
        if (event.timelineEventType === 'ThirdPartyListingsPublished') {
          return DisplayEvent(
            {
              ...event,
              metadata: {
                ...event.metadata,
                externalPublishing:
                  unitExternalPublishing?.externalListingPublishings,
              },
            },
            index === firstNonInternalIndex,
            timeZone,
            true,
            { onReschedule: openReschedule },
          );
        }

        return DisplayEvent(
          event,
          index === firstNonInternalIndex,
          timeZone,
          true,
          { onReschedule: openReschedule },
        );
      }),
    ),
  ].filter((item) => !isNil(item));
  let hasShownEventHelp = false;
  const allTimelineItems = timelineItems.map((item) => {
    if (!item?.right?.props?.adornment) return item;
    if (!hasShownEventHelp) {
      hasShownEventHelp = true;
      return item;
    }
    return {
      ...item,
      right: cloneElement(item.right, { adornment: undefined }),
    };
  });

  const nextRequiredEventsArray = Object.values(
    timeline.requiredEventsForNextMilestone,
  ).filter(
    (event) => !NEXT_REQUIRED_EVENTS_BLACK_LIST[event.timelineEventType],
  );

  const employeesWithOrder = employees?.map((employee) => ({
    ...employee,
    order: [
      EmployeeAssignmentTypes.HomeownerGrowth,
      EmployeeAssignmentTypes.Concierge,
    ].includes(employee.assignmentType),
  }));

  return (
    <div className="UnitWrapper">
      <AccountUnitDetailContainer accountUnitDetail={unit} />
      <Space value={SPACE_TYPES.XXXL} />

      {employees.length > 0 && (
        <>
          <AccountPageSection
            title="Meet Your Team"
            content={
              <div className={cx('multiple-employee-container')}>
                <MultipleBelongEmployeeProfileView
                  employees={employeesWithOrder}
                />
              </div>
            }
          />
          <Space value={SPACE_TYPES.XL} />
        </>
      )}

      <div className={cx('tabs')}>
        <TabBar
          name="unitTabBar"
          // Show only first item if no lease.
          items={currentLease || nextLease ? TAB_ITEMS : [TAB_ITEMS[0]]}
          onSelect={setSelectedTab}
          selected={selecetedTab.key}
        />
      </div>

      {selecetedTab.key === 'timeline' && (
        <>
          {/* If there are no events in nextRequiredEventsArray, show next milestone */}
          {(nextRequiredEventsArray.length > 0 ||
            NEXT_MILESTONE_MAP[timeline.nextMilestone]) && (
            <>
              <Text fontWeight="semibold" color="dark-gray">
                What’s next?
              </Text>
              <Space value={SPACE_TYPES.XS} />
              <NextRequiredEvents
                nextRequiredEvents={
                  nextRequiredEventsArray.length > 0
                    ? nextRequiredEventsArray
                    : [{ name: NEXT_MILESTONE_MAP[timeline.nextMilestone] }]
                }
              />
              <Space value={SPACE_TYPES.SM} />
            </>
          )}
          <AccountTimeline
            className={cx('padding-for-pulse')}
            items={allTimelineItems}
            includesEndTail={false}
            showAll
            dashedLines
          />
        </>
      )}
      {selecetedTab.key === 'residents' && (
        <>
          {/* We are hidding this section from consumer. This will be displayed again once Deposit Free Fee and Renter Insurance costs are corrected. */}
          {/* <CurrentLease currentLease={currentLease} /> */}
          <NextLease nextLease={nextLease} />
          {allResidentsLen > 0 && (
            <AccountPageSection
              title="Belong Members"
              content={<ResidentListContainer residents={allResidents} />}
            />
          )}
        </>
      )}
      {rescheduleProperty && (
        <RescheduleAppointmentModal
          coverageRegionId={rescheduleProperty.basicInfo.coverageRegionId}
          isHandledByAnExternalProvider={
            rescheduleInspection?.isHandledByAnExternalProvider || false
          }
          isOpen={isRescheduleOpen}
          onDismiss={closeReschedule}
          timeZone={timeZone}
          unit={unit}
          unitId={unitId}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  externalPublishings: selectAccountUnitExternalPublishings(state),
  properties: selectAccountProperties(state),
});

Unit.propTypes = {
  unit: PropTypes.object.isRequired,
  employees: PropTypes.array.isRequired,
  timeline: PropTypes.object.isRequired,
  externalPublishings: PropTypes.object,
  properties: PropTypes.array,
};

export default connect(mapStateToProps)(Unit);
